import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, Loader2, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  value: string[];
  onValueChange: (value: string[]) => void;
  options: MultiSelectOption[];
  id?: string;
  ariaLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onOpenChange?: (open: boolean) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
}

/** How many selected ids render as individual removable chips. */
const MAX_CHIPS = 4;

/**
 * Searchable multi-select built on Radix Popover. Renders a checkbox list with
 * a "Select All Active Projects" master checkbox (checked / unchecked /
 * indeterminate), free-text search that only affects visibility, and removable
 * chips below the trigger. Keyboard navigation works through the trigger
 * button, the search input and native checkboxes.
 */
export function MultiSelect({
  value,
  onValueChange,
  options,
  id,
  ariaLabel = 'Projects',
  placeholder = 'Select projects',
  disabled,
  isLoading = false,
  isError = false,
  onRetry,
  onOpenChange,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No options available',
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const selectAllRef = React.useRef<HTMLInputElement>(null);

  const selectedCount = value.length;
  const allChecked = selectedCount > 0 && selectedCount === options.length;
  const indeterminate = selectedCount > 0 && selectedCount < options.length;

  React.useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const normalized = query.trim().toLowerCase();
  const visible = normalized
    ? options.filter((o) => o.label.toLowerCase().includes(normalized))
    : options;

  const toggle = (optValue: string) => {
    onValueChange(value.includes(optValue) ? value.filter((v) => v !== optValue) : [...value, optValue]);
  };

  const toggleAll = () => {
    onValueChange(allChecked ? [] : options.map((o) => o.value));
  };

  const triggerLabel = isLoading
    ? 'Loading projects...'
    : isError
      ? 'Unable to load projects'
      : selectedCount === 0
        ? placeholder
        : selectedCount === 1
          ? (options.find((o) => o.value === value[0])?.label ?? `${selectedCount} project selected`)
          : `${selectedCount} projects selected`;

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
        onOpenChange?.(next);
      }}
    >
      <Popover.Trigger asChild disabled={disabled}>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          className={cn(
            'flex h-10 w-full max-w-full min-w-0 items-center justify-between rounded-lg border bg-surface px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50 text-primary',
            open && 'border-brand-500 ring-1 ring-brand-500'
          )}
        >
          <span className={cn('truncate text-left', selectedCount === 0 && !isLoading && !isError && 'text-muted')}>
            {triggerLabel}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[var(--radix-popover-trigger-width)] max-w-[90vw] rounded-lg border bg-surface p-1 text-primary shadow-md"
        >
          {isLoading ? (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted">
              <Loader2 size={14} className="animate-spin" />
              Loading active projects...
            </div>
          ) : isError ? (
            <div className="px-3 py-3 text-sm">
              <p className="text-red-500">Unable to load active projects.</p>
              {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry} className="mt-2">
                  Retry
                </Button>
              )}
            </div>
          ) : options.length === 0 ? (
            <p className="px-3 py-3 text-sm text-muted">{emptyMessage}</p>
          ) : (
            <>
              <div className="mb-1 flex items-center gap-2 rounded-md border px-2 py-1.5">
                <Search className="h-4 w-4 shrink-0 opacity-50" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault();
                    if (e.key === 'Escape') setOpen(false);
                  }}
                  placeholder={searchPlaceholder}
                  className="w-full min-w-0 bg-transparent text-sm outline-none placeholder:text-muted"
                />
              </div>
              <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-hover">
                <input
                  type="checkbox"
                  ref={selectAllRef}
                  checked={allChecked}
                  onChange={toggleAll}
                  className="h-4 w-4 accent-blue-600"
                />
                <span className="font-medium">Select All Active Projects</span>
              </label>
              <div className="mt-1 max-h-56 overflow-y-auto">
                {visible.length === 0 ? (
                  <p className="px-2 py-2 text-sm text-muted">No matching projects.</p>
                ) : (
                  visible.map((opt) => (
                    <label key={opt.value} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-hover">
                      <input
                        type="checkbox"
                        checked={value.includes(opt.value)}
                        onChange={() => toggle(opt.value)}
                        className="h-4 w-4 accent-blue-600"
                      />
                      <span className="truncate">{opt.label}</span>
                    </label>
                  ))
                )}
              </div>
            </>
          )}
        </Popover.Content>
      </Popover.Portal>

      {selectedCount > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {selectedCount <= MAX_CHIPS ? (
            value.map((v) => {
              const opt = options.find((o) => o.value === v);
              const label = opt?.label ?? v;
              return (
                <span
                  key={v}
                  className="inline-flex max-w-full items-center gap-1 rounded-full border bg-surface-secondary px-2 py-0.5 text-xs text-primary"
                >
                  <span className="truncate">{label}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${label}`}
                    onClick={() => toggle(v)}
                    className="rounded-full p-0.5 text-muted transition-colors hover:bg-hover hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    <X size={12} />
                  </button>
                </span>
              );
            })
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full border bg-surface-secondary px-2 py-0.5 text-xs text-primary">
              {selectedCount} projects selected
              <button
                type="button"
                aria-label="Clear all projects"
                onClick={() => onValueChange([])}
                className="rounded-full p-0.5 text-muted transition-colors hover:bg-hover hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                <X size={12} />
              </button>
            </span>
          )}
        </div>
      )}
    </Popover.Root>
  );
}
